////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.3                                                            //
//  Summary: The single source of motion truth (1.0.3.md §2) — easing,       //
//           durations, stagger, shared variants and the reduced-motion       //
//           collapse helper every one of them must be passed through.       //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////
//
//  Every animated component in this app imports its transition curve, its
//  durations and its variants from HERE — never inline. 1.0.3.md §2 (and its §7
//  acceptance criterion) is explicit: "no component defines its own curve."
//  Pre-existing ad hoc motion (components/content/Carousel.tsx,
//  components/chrome/MenuOverlay.tsx) predates this file and is intentionally left
//  untouched by this pass — migrating them onto this system is restyling work for
//  a sibling agent, not this foundations phase.
//
//  Reduced motion: every variant below is written once, in its full form. Callers
//  MUST run it through `reduceVariants()` (passing the result of
//  `hooks/useReducedMotion.ts`) before handing it to framer-motion — that is the
//  ONE place the opacity-only, <=150ms collapse is implemented, so a consumer
//  cannot quietly skip it by hand-rolling their own reduced-motion branch (1.0.3.md
//  §2/§7: "every variant collapses to opacity-only <=150ms... non-negotiable").
//
////////////////////////////////////////////////////////////////////////////////

import type { TargetAndTransition, Transition, Variants } from 'framer-motion';

// ---------------------------------------------------------------------------
// Easing & durations — the one dialect, everywhere (1.0.3.md §2)
// ---------------------------------------------------------------------------

/** The only easing curve this app uses, full stop. */
export const EASING: [number, number, number, number] = [0.22, 1, 0.36, 1];

export const DURATION = {
  /** Hover/interaction feedback — card lift, imagery scale (§2). */
  hover: 0.2,
  /** Lower bound of the 300-450ms entrance band. */
  fast: 0.3,
  /** Mid-band default for most entrances (fadeRise, maskRevealWord). */
  base: 0.35,
  /** Upper bound of the entrance band — reserved for the heavier reveals
   * (clipWipe on large imagery). */
  slow: 0.45,
  /** Route/page content crossfade (§5/§6) — travels WITH crossfadeBackground
   * below, just on its own, shorter, clock. */
  crossfadeContent: 0.35,
  /** <BackgroundLayer /> route crossfade (§5) — deliberately slower than the
   * content above it so the background reads as a calm backdrop, not a flicker. */
  crossfadeBackground: 0.6,
  /** The collapsed-for-reduced-motion ceiling — nothing reduced-motion ever
   * animates for longer than this. */
  reduced: 0.15,
} as const;

/** 60-80ms band midpoint (§2) — staggerGroup's default `staggerChildren`. */
export const STAGGER_CHILDREN = 0.07;

/** Scroll-entrance viewport contract (§2): `whileInView` fires once, slightly
 * before the element is fully on-screen, and never re-fires on scroll-back. */
export const VIEWPORT_ONCE = { once: true, margin: '-10%' } as const;

// ---------------------------------------------------------------------------
// Entrance / reveal variants
// ---------------------------------------------------------------------------

const ENTRANCE_TRANSITION: Transition = { duration: DURATION.base, ease: EASING };

/** Default entrance for most content — glass tiles, cards, generic blocks. */
export const fadeRise: Variants = {
  hidden: { opacity: 0, y: 16, scale: 0.98 },
  visible: { opacity: 1, y: 0, scale: 1, transition: ENTRANCE_TRANSITION },
};

/** Wraps a group of `fadeRise` (or similar) children — apply to the parent,
 * `hidden`/`visible` on each child, and framer-motion's own propagation staggers
 * them. `delayChildren` gives the container itself a beat before the first child
 * moves, so the group doesn't read as simultaneous with its own entrance. */
export const staggerGroup: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: STAGGER_CHILDREN, delayChildren: STAGGER_CHILDREN / 2 },
  },
};

/** Word-level baseline clip reveal for display headings (the h1-huge hero copy,
 * §4). Intended per-word: wrap each word in its own `overflow-hidden` span and
 * animate this on the inner span — the clip-path is what gives the "rising from
 * the baseline" read, not a plain fade. */
export const maskRevealWord: Variants = {
  hidden: { opacity: 0, y: '100%', clipPath: 'inset(0% 0% 100% 0%)' },
  visible: {
    opacity: 1,
    y: '0%',
    clipPath: 'inset(0% 0% 0% 0%)',
    transition: ENTRANCE_TRANSITION,
  },
};

/** Inset clip-path reveal for imagery (project/news cards, capability shots) —
 * left-to-right wipe rather than a fade, per §2. Uses the slower end of the
 * entrance band since it is usually revealing a larger element. */
export const clipWipe: Variants = {
  hidden: { clipPath: 'inset(0% 100% 0% 0%)' },
  visible: {
    clipPath: 'inset(0% 0% 0% 0%)',
    transition: { duration: DURATION.slow, ease: EASING },
  },
};

// ---------------------------------------------------------------------------
// Crossfade — page content & <BackgroundLayer /> (§5/§6)
// ---------------------------------------------------------------------------

/** AnimatePresence-driven page content transition — 12px drift + crossfade,
 * coordinated with `crossfadeBackground` below by sharing the same easing (each
 * runs its own clock/duration per §5/§6, not the same `motion.div`). */
export const crossfadeContent: Variants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: DURATION.crossfadeContent, ease: EASING } },
  exit: { opacity: 0, y: -12, transition: { duration: DURATION.crossfadeContent, ease: EASING } },
};

/** `<BackgroundLayer />`'s per-route crossfade — opacity only (no drift; a moving
 * backdrop under moving content is exactly the "flash-bang" this phase is
 * explicitly avoiding, §0). */
export const crossfadeBackground: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: DURATION.crossfadeBackground, ease: EASING } },
  exit: { opacity: 0, transition: { duration: DURATION.crossfadeBackground, ease: EASING } },
};

// ---------------------------------------------------------------------------
// Hover grammar (§2) — applied via framer-motion's `whileHover`, not a variant
// keyed object (hover has no "hidden" state to key against).
// ---------------------------------------------------------------------------

/** Card-level hover: 4px lift + shadow deepen. Apply to the card's own
 * `motion.*` element via `whileHover={reduceHover(cardHover, reducedMotion)}`. */
export const cardHover: TargetAndTransition = {
  y: -4,
  boxShadow: '0 24px 48px -24px rgb(0 0 0 / 0.35)',
  transition: { duration: DURATION.hover, ease: EASING },
};

/** Imagery-level hover, nested inside a `cardHover` card — the photo scales
 * independently of the card's own lift. */
export const cardHoverImage: TargetAndTransition = {
  scale: 1.03,
  transition: { duration: DURATION.hover, ease: EASING },
};

/** Hover/focus-revealed text label sat NEXT TO an icon control (e.g.
 * ThemeToggle's "Dark mode"/"Light mode", 1.0.3 post-review tweak #2) — a
 * horizontal fade+slide rather than `fadeRise`'s vertical one, since the label
 * is appearing beside its icon, not rising into a scroll-entrance. Callers
 * MUST render the label absolutely-positioned against a fixed-size sibling box
 * (never in normal flow) so the reveal never shifts layout — this variant only
 * owns the motion, not the positioning contract. Runs on the same `DURATION.
 * hover`/`EASING` band as the rest of §2's hover grammar, since it is a hover
 * (or focus) interaction, not a scroll entrance. */
export const labelReveal: Variants = {
  hidden: { opacity: 0, x: -6 },
  visible: { opacity: 1, x: 0, transition: { duration: DURATION.hover, ease: EASING } },
};

// ---------------------------------------------------------------------------
// Ambient & imperative motion (§2/§7) — still owned HERE
// ---------------------------------------------------------------------------
//
// These are not entrance `Variants`, so they cannot be expressed as one of the
// keyed objects above — but §7's "no component defines its own curve" applies to
// them just the same, so the constants live here rather than in the component
// that happens to use them.

/** Ambient "breathing" pulse for the hero CTA's arrows (§4). A symmetric,
 * infinitely-repeating loop needs a symmetric easeInOut — deliberately NOT
 * EASING, which is a one-shot entrance curve — and `reduceVariants`' contract
 * (opacity-only, <=150ms, no repeat) does not model a loop at all. Consumers
 * pick the reduced branch via `ambientPulse(reducedMotion)` below. */
export const AMBIENT_PULSE_DURATION = 2;
export const AMBIENT_PULSE_EASE = 'easeInOut' as const;

/** The hero CTA arrow breathe, already reduced-motion resolved: a static, fully
 * opaque arrow (no loop at all) when the visitor has asked for less motion. */
export function ambientPulse (reducedMotion: boolean): {
  animate: TargetAndTransition;
  transition: Transition;
} {
  if (reducedMotion) return { animate: { opacity: 1 }, transition: { duration: 0 } };
  return {
    animate: { opacity: [0.5, 1, 0.5] },
    transition: { duration: AMBIENT_PULSE_DURATION, repeat: Infinity, ease: AMBIENT_PULSE_EASE },
  };
}

/** Spring used by the <StatsTicker /> count-up (§6) — framer-motion's imperative
 * `animate()` takes a spring config rather than a variant, so it is pinned here
 * alongside every other motion constant instead of inline in the component. */
export const COUNT_UP_SPRING = { type: 'spring', stiffness: 60, damping: 18 } as const;

// ---------------------------------------------------------------------------
// Reduced-motion collapse — the ONE helper (§2/§7)
// ---------------------------------------------------------------------------

/** Collapses any keyed `Variants` object (states named `hidden`/`visible`,
 * `initial`/`animate`/`exit`, or any other custom keys) to an opacity-only,
 * <=150ms transition per state when `reducedMotion` is true — otherwise returns
 * `base` untouched. Every exported variant above is designed to be run through
 * this before use; nothing here mutates `base`.
 *
 * Each collapsed state keeps `base`'s own opacity value (falling back to `1` for
 * states — e.g. `visible`/`animate` — that don't set one) so the reveal itself
 * still happens, just as a fade rather than a transform. States expressed as a
 * dynamic variant function (framer-motion's per-index/custom-prop form) are
 * passed through unchanged — this app's variants above are all static objects,
 * so that branch exists for defensive completeness rather than current use. */
export function reduceVariants<T extends Variants> (base: T, reducedMotion: boolean): T {
  if (!reducedMotion) return base;

  const collapsed: Variants = {};
  for (const [key, state] of Object.entries(base)) {
    if (typeof state === 'function') {
      collapsed[key] = state;
      continue;
    }
    const opacity = typeof state.opacity === 'number' ? state.opacity : 1;
    collapsed[key] = { opacity, transition: { duration: DURATION.reduced, ease: EASING } };
  }
  return collapsed as T;
}

/** Same contract as `reduceVariants`, for a standalone hover target (`cardHover`,
 * `cardHoverImage`) rather than a keyed variants object — reduced motion means no
 * hover motion at all (an empty target), not a faster version of it. */
export function reduceHover (target: TargetAndTransition, reducedMotion: boolean): TargetAndTransition {
  return reducedMotion ? {} : target;
}
